import { useTranslation } from 'react-i18next';
import { Banknote, Info, Smartphone, TriangleAlert, Wallet } from 'lucide-react';
import { formatIqd, fromMillidinars, subtract, type BoxSplit, type Millidinars } from '@gc/shared';

interface BoxSplitFiguresProps {
  split: BoxSplit;
  currencySymbol: string;
  /** Compact layout for the reset confirmation, where space is tight. */
  dense?: boolean;
}

/**
 * The box figure, always in three parts — SPEC §12.1.
 *
 * The owner accepted that the box counts account-balance payments as well as
 * cash, which makes it a total of business handled rather than notes in hand.
 * The consequence they also accepted is that the drawer will be light by
 * exactly the account component at handover.
 *
 * So this component never renders the total on its own. A single blended number
 * cannot be checked against the till, and an employee asked to sign off one
 * would be signing off an apparent shortfall that is not a shortfall. Every
 * surface that shows a box figure renders it through here, so there is no path
 * by which the split can be dropped from one screen and not another.
 *
 * A FOURTH figure since 2026-09-24: money taken through a payment provider
 * (FIB, Qi, FastPay). No note entered the drawer for it either, so it is its
 * own line — and only when the box holds some, so a venue that takes no
 * electronic payments sees the three figures it always saw.
 */
export function BoxSplitFigures({ split, currencySymbol, dense = false }: BoxSplitFiguresProps) {
  const { t } = useTranslation();

  const total = fromMillidinars(split.totalMd);
  const cash = fromMillidinars(split.cashMd);
  const account = fromMillidinars(split.accountBalanceMd);
  // `?? '0'`: a server from before the third component sends none.
  const electronic = fromMillidinars(split.electronicMd ?? '0');
  const gap = subtract(total, cash);
  const money = (value: Millidinars) => formatIqd(value, { currencySymbol });

  const hasAccountComponent = account !== 0n;
  const hasElectronicComponent = electronic !== 0n;

  return (
    <div className="rounded-card border border-subtle bg-raised">
      <dl
        className={
          dense
            ? 'divide-y divide-subtle'
            : `grid gap-px bg-subtle ${hasElectronicComponent ? 'sm:grid-cols-4' : 'sm:grid-cols-3'}`
        }
      >
        <Figure
          label={t('boxBalance.total')}
          value={money(total)}
          emphasis
          dense={dense}
          icon={<Wallet className="h-4 w-4" aria-hidden />}
        />
        <Figure
          label={t('boxBalance.cash')}
          value={money(cash)}
          dense={dense}
          icon={<Banknote className="h-4 w-4" aria-hidden />}
        />
        <Figure
          label={t('boxBalance.accountBalance')}
          value={money(account)}
          dense={dense}
          icon={<Info className="h-4 w-4" aria-hidden />}
        />
        {hasElectronicComponent ? (
          <Figure
            label={t('boxBalance.electronic')}
            value={money(electronic)}
            dense={dense}
            icon={<Smartphone className="h-4 w-4" aria-hidden />}
          />
        ) : null}
      </dl>

      <div className="border-t border-subtle p-3 text-xs leading-relaxed text-content-secondary">
        <p>{t('boxBalance.splitExplainer')}</p>

        {hasElectronicComponent ? (
          <p data-electronic-explainer className="mt-2 flex items-start gap-2 text-ending">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              {t('boxBalance.electronicExplainer', {
                cash: money(cash),
                total: money(total),
                account: money(account),
                electronic: money(electronic),
              })}
            </span>
          </p>
        ) : hasAccountComponent ? (
          <p className="mt-2 flex items-start gap-2 text-ending">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              {t('boxBalance.shortfallExplainer', {
                cash: money(cash),
                total: money(total),
                gap: money(gap),
              })}
            </span>
          </p>
        ) : (
          <p className="mt-2 text-content-muted">{t('boxBalance.pureCash')}</p>
        )}

        <p className="tabular mt-2 text-content">
          {t('boxBalance.cashInHand')}: <strong>{money(cash)}</strong>
        </p>
      </div>
    </div>
  );
}

function Figure({
  label,
  value,
  icon,
  emphasis,
  dense,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  emphasis?: boolean;
  dense: boolean;
}) {
  return (
    <div
      className={
        dense
          ? 'flex items-center justify-between px-3 py-2'
          : 'flex flex-col gap-1 bg-raised px-4 py-3'
      }
    >
      <dt className="flex items-center gap-1.5 text-xs text-content-secondary">
        {icon}
        {label}
      </dt>
      <dd
        className={`tabular ${emphasis ? 'text-xl font-semibold text-content' : 'text-base text-content-secondary'}`}
      >
        {value}
      </dd>
    </div>
  );
}
